import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, User, Search, Menu, X, Heart, Car } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import LanguageSwitcher from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';

const Navbar = ({ cartCount = 0 }) => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery('');
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex gap-6">
              <span>{t('nav.free_delivery')}</span>
              <span className="hidden md:inline">|</span>
              <span className="hidden md:inline">{t('nav.products_count')}</span>
            </div>
            <div className="flex gap-4">
              <LanguageSwitcher />
              <button className="hover:text-orange-200 transition-colors">{t('nav.help')}</button>
              <button className="hover:text-orange-200 transition-colors">{t('nav.contact')}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-lg font-bold text-xl">
              MEGA parts OMAN
            </div>
          </Link>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearch} className="w-full">
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t('nav.search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 h-11"
                  dir="ltr"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>

          {/* Right Icons */}
          <div className="flex items-center space-x-4">
            <button className="hidden md:flex items-center gap-2 hover:text-blue-600 transition-colors" onClick={() => navigate('/wishlist')}>
              <Heart className="h-5 w-5" />
              <span className="text-sm">{t('nav.wishlist')}</span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/login')}>
                  {t('auth.sign_in')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/register')}>
                  {t('auth.sign_up')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-account')}>
                  {t('account.my_orders')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/cart">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-orange-500 hover:bg-orange-600">
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </Link>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-4">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Input
                type="text"
                placeholder={t('nav.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10"
                dir="ltr"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
              >
                <Search className="h-5 w-5 text-gray-400" />
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/products"
              className="block py-2 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('products.all_products')}
            </Link>
            <Link
              to="/vehicle-selector"
              className="block py-2 hover:text-blue-600 transition-colors font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              🚗 {t('nav.vehicle_selector')}
            </Link>
            <Link
              to="/vin-lookup"
              className="block py-2 hover:text-orange-600 transition-colors font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              🔍 {t('nav.vin_decoder')}
            </Link>
            <Link
              to="/wishlist"
              className="block py-2 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.wishlist')}
            </Link>
            <Link
              to="/my-account"
              className="block py-2 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('account.my_account')}
            </Link>
          </div>
        </div>
      )}
      
      {/* Secondary Navigation - Desktop */}
      <div className="hidden md:block border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            <div className="flex gap-6">
              <Link
                to="/products"
                className="text-sm font-medium hover:text-orange-600 transition-colors"
              >
                {t('products.all_products')}
              </Link>
              <Link
                to="/vehicle-selector"
                className="text-sm font-medium hover:text-blue-600 transition-colors flex items-center gap-2"
              >
                <Car className="h-4 w-4" />
                {t('nav.vehicle_selector')}
              </Link>
              <Link
                to="/vin-lookup"
                className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-2"
              >
                <Car className="h-4 w-4" />
                {t('nav.vin_decoder')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
